import {useEffect, useId, useRef, useState} from 'react'
import {FloatingLabel, Form} from 'react-bootstrap'
import Overlay from 'react-bootstrap/Overlay'
import Popover from 'react-bootstrap/Popover'
import {DayPicker} from 'react-day-picker'
import 'react-day-picker/style.css'
import './weekCalendar.css'
import {
  endOfWeek,
  format,
  getHours,
  getMinutes,
  isValid,
  setHours,
  setMinutes,
  startOfDay,
  startOfWeek,
} from 'date-fns'
import {WeekDays} from '../../helpers/generals'
import {focusCalendarDay, weekLabel} from '../nav/DateNavigator'

const minutesOfDay = (d: Date) => getHours(d) * 60 + getMinutes(d)

/**
 * Whether a time-of-day is offered, with react-datepicker's minTime/maxTime
 * semantics — the ones the shift editor's bounds were written against: only
 * the clock time is compared, and a window whose max is BEFORE its min (hours
 * of operation past midnight) restricts nothing.
 */
export const isTimeAllowed = (time: Date, minTime?: Date, maxTime?: Date) => {
  if (!minTime || !maxTime) return true
  const t = minutesOfDay(time)
  const min = minutesOfDay(minTime)
  const max = minutesOfDay(maxTime)
  if (max < min) return true
  return t >= min && t <= max
}

const withTimeOf = (day: Date, time: Date) =>
  setMinutes(setHours(day, getHours(time)), getMinutes(time))

interface DateTimeFieldProps {
  label: string
  value: Date | string
  onChange(date: Date): void
  weekStartOn: WeekDays
  minDate?: Date
  maxDate?: Date
  minTime?: Date
  maxTime?: Date
  // Minutes between the offered times.
  interval?: number
  // 'week': no time column; a click anywhere in a row picks that week (the
  // value is the clicked day) and the field shows the week's range.
  pick?: 'datetime' | 'week'
  id?: string
}

/**
 * A date + time field: the calendar the board's navigator uses, with a time
 * column beside it. Picking a day keeps the time; picking a time closes it.
 *
 * Replaces react-datepicker's showTimeSelect, keeping its contract (day-level
 * minDate/maxDate, clock-level minTime/maxTime, 30-minute steps) so the shift
 * editor's bounds mean what they meant.
 */
const DateTimeField = ({
  label,
  value,
  onChange,
  weekStartOn,
  minDate,
  maxDate,
  minTime,
  maxTime,
  interval = 30,
  pick = 'datetime',
  id,
}: DateTimeFieldProps) => {
  const date = new Date(value)
  // new Date(null) is 1970, a valid date; a missing value is not one.
  const valid = value != null && isValid(date)
  // Without an id the floating label is not tied to its input.
  const autoId = useId()
  const fieldId = id || autoId
  const [open, setOpen] = useState(false)
  const [hovered, setHovered] = useState<Date | null>(null)
  const [month, setMonth] = useState<Date>(valid ? date : new Date())
  const target = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  // The popover renders INSIDE the field's wrapper, not at <body>: these
  // fields live in react-bootstrap modals, whose focus trap pulls focus back
  // out of anything portalled outside them — the calendar's keyboard
  // navigation would never get focus.
  const container = useRef<HTMLDivElement>(null)
  const selectedTimeRef = useRef<HTMLButtonElement>(null)
  const timeListRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Bring the chosen time into view instead of opening the list at midnight.
    // Scrolls the list only — scrollIntoView would also move the modal/page.
    const list = timeListRef.current
    const item = selectedTimeRef.current
    if (open && list && item) {
      list.scrollTop = item.offsetTop - list.offsetTop - list.clientHeight / 2
    }
  }, [open])

  const base = valid ? date : new Date()
  // Each slot is set as a clock time on the day, not counted in minutes from
  // midnight: on a daylight-saving day the count drifts an hour (on the
  // fall-back day 1 AM came twice and 11 PM never). On the spring-forward day
  // 2 AM does not exist and lands on 3 AM, so repeats are dropped.
  const times: Date[] = []
  for (let m = 0; m < 24 * 60; m += interval) {
    const t = setMinutes(setHours(startOfDay(base), Math.floor(m / 60)), m % 60)
    const last = times[times.length - 1]
    if (last && last.getTime() >= t.getTime()) continue
    if (isTimeAllowed(t, minTime, maxTime)) times.push(t)
  }

  const disabled = [
    ...(minDate ? [{before: startOfDay(minDate)}] : []),
    ...(maxDate ? [{after: maxDate}] : []),
  ]

  return (
    <>
      <div ref={target}>
        <FloatingLabel controlId={fieldId} label={label}>
          <Form.Control
            ref={inputRef}
            readOnly
            value={
              !valid
                ? ''
                : pick === 'week'
                ? weekLabel(
                    startOfWeek(date, {weekStartsOn: weekStartOn}),
                    endOfWeek(date, {weekStartsOn: weekStartOn}),
                    undefined
                  )
                : format(date, 'M/d/yy h:mm aa')
            }
            onClick={() => {
              if (!open) setMonth(base)
              setOpen(!open)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
                e.preventDefault()
                setMonth(base)
                setOpen(true)
              }
            }}
            style={{cursor: 'pointer'}}
            aria-haspopup='dialog'
            aria-expanded={open}
          />
        </FloatingLabel>
      </div>
      <div ref={container} />
      <Overlay
        target={target.current}
        container={container.current}
        popperConfig={{strategy: 'fixed'}}
        onEntered={() => focusCalendarDay(`${fieldId}-picker`)}
        show={open}
        placement='bottom-start'
        rootClose
        onHide={() => setOpen(false)}
      >
        <Popover
          id={`${fieldId}-picker`}
          className='shadow-sm'
          style={{maxWidth: 'none'}}
          onKeyDown={(e) => {
            // Esc closes the calendar only. Without preventDefault the
            // surrounding modal takes it as its own Esc and closes, edits and all.
            if (e.key === 'Escape') {
              e.preventDefault()
              e.stopPropagation()
              setOpen(false)
              inputRef.current?.focus()
            }
          }}
        >
          <Popover.Body className='p-3 d-flex gap-3'>
            <DayPicker
              className='fds-calendar'
              month={month}
              onMonthChange={setMonth}
              weekStartsOn={weekStartOn}
              showOutsideDays
              fixedWeeks
              disabled={disabled}
              showWeekNumber={pick === 'week'}
              onDayClick={(day, modifiers) => {
                if (modifiers.disabled) return
                if (pick === 'week') {
                  onChange(day)
                  setOpen(false)
                } else {
                  onChange(withTimeOf(day, base))
                }
              }}
              onDayMouseEnter={(day) => setHovered(day)}
              onDayMouseLeave={() => setHovered(null)}
              onDayFocus={(day) => setHovered(day)}
              modifiers={
                pick === 'week'
                  ? {
                      weekSelected: valid
                        ? {
                            from: startOfWeek(date, {weekStartsOn: weekStartOn}),
                            to: endOfWeek(date, {weekStartsOn: weekStartOn}),
                          }
                        : [],
                      weekHover: hovered
                        ? {
                            from: startOfWeek(hovered, {weekStartsOn: weekStartOn}),
                            to: endOfWeek(hovered, {weekStartsOn: weekStartOn}),
                          }
                        : [],
                      weekFirst: {dayOfWeek: [weekStartOn]},
                      weekLast: {dayOfWeek: [(weekStartOn + 6) % 7]},
                    }
                  : {daySelected: valid ? date : []}
              }
              modifiersClassNames={{
                daySelected: 'fds-day-selected',
                weekSelected: 'fds-week-selected',
                weekHover: 'fds-week-hover',
                weekFirst: 'fds-week-first',
                weekLast: 'fds-week-last',
              }}
            />
            {pick === 'datetime' && (
              <div
                ref={timeListRef}
                className='d-flex flex-column border-start ps-3 overflow-auto'
                style={{maxHeight: 300, minWidth: 96}}
                role='listbox'
                aria-label='Time'
              >
                {times.map((t) => {
                  const selected = valid && minutesOfDay(t) === minutesOfDay(date)
                  return (
                    <button
                      key={t.getTime()}
                      ref={selected ? selectedTimeRef : undefined}
                      type='button'
                      role='option'
                      aria-selected={selected}
                      className={`btn btn-sm text-nowrap text-start px-3 py-1 ${
                        selected ? 'btn-primary' : 'btn-active-light-primary text-gray-800'
                      }`}
                      onClick={() => {
                        onChange(withTimeOf(base, t))
                        setOpen(false)
                      }}
                    >
                      {format(t, 'h:mm aa')}
                    </button>
                  )
                })}
              </div>
            )}
          </Popover.Body>
        </Popover>
      </Overlay>
    </>
  )
}

export {DateTimeField}
